import { recordUserAction } from "@/components/activity/ducks/Actions"
import isEmpty from "@/components/helpers/isEmpty"
import { encodeQueryString, getUrlVars } from "@/components/helpers/URL"
import { NavigationActions } from "@/components/navigation/ducks/slice"
import {
  CLICK_SEARCH_CARD,
  clearSelectedUser,
  FETCH_MAP_LOCATIONS,
  FETCH_RECENT_SEARCHES,
  FETCH_SEARCH_TEXT_SUGGESTIONS,
  FETCH_SELECTED_USER,
  FETCH_SUGGESTED_SEARCHES,
  FETCH_USER_SEARCH,
  FETCH_USER_SEARCH_NEXT_PAGE,
  fetchMapLocations,
  fetchRecentSearches,
  fetchSelectedUser,
  fetchSuggestedSearches,
  RECEIVE_USER_SEARCH,
  REFETCH_SEARCH,
  receiveInitialSearchError,
  receiveMapLocations,
  receiveNextPageError,
  receiveRecentSearches,
  receiveSearchSuggestions,
  receiveSelectedUser,
  receiveSuggestedSearches,
  receiveUserSearch,
  receiveUserSearchFinal,
  SET_SELECTED_USER,
  SET_SELECTED_USER_URL,
  setSelectedUserUrl,
} from "@/components/search/ducks/Actions"
import {
  apiFetchMapLocations,
  apiFetchRecentSearches,
  apiFetchSearchSuggestions,
  apiFetchSelectedUser,
  apiFetchSuggestedSearches,
  fetchSearchResults,
} from "@/components/search/ducks/AJAX"
import findSelectedUser from "@/components/search/helpers/findSelectedUser"
import { SearchDrawerActions } from "@/features/volunteer-roles/search-drawer.store"
import { VolunteerRolesActions } from "@/features/volunteer-roles/store"
import store from "@/store/store"
import { addSuffix } from "middleware/helpers/Suffix"
import _history from "routing/history"
import handlePagination from "../helpers/handlePagination"

// was in reducer, moved here in middleware due to https://redux.js.org/Errors?code=3
const updateExplanation = (oldState, newState) => {
  const { results, filters, loadingFilteredSearch } = newState
  const { originalQueryTotal, wealthAggregations } = store.getState().wealth.admin
  const { query, explanation, total } = results
  const { displayExplanation } = { oldState }

  if (loadingFilteredSearch && displayExplanation) {
    return displayExplanation
  }

  if (
    isEmpty(filters) ||
    wealthAggregations.length === 0 ||
    !originalQueryTotal ||
    total === originalQueryTotal
  ) {
    return explanation
  }

  const baseExplanation = `Selected ${total} of ${originalQueryTotal} results`

  return query ? `${baseExplanation} for ${query}` : baseExplanation
}
export default (store) => {
  let selectedProfileRequestGeneration = 0

  return (next) => (originalAction) => {
    const action = originalAction
    const { dispatch } = store

    const receiveLocationsSuccess = (cb) => (resp) => {
      dispatch(receiveMapLocations(resp.locations, resp.query))
      if (cb) {
        cb()
      }
    }

    const receiveRefetchSearchSuccess = (cb) => (response) => {
      if (cb) {
        cb()
      }
      dispatch(receiveUserSearch(response))
      dispatch(fetchSuggestedSearches())
    }

    const receiveFetchUserSearch = (selectedUserId, filters, cb) => (response) => {
      dispatch(receiveUserSearch(response, selectedUserId, filters))
      if (cb) {
        cb()
      }
    }

    const receiveInitialSearchErrorMW = (response) => dispatch(receiveInitialSearchError(response))
    const receiveSearchResultsSuccess = (response) => dispatch(receiveUserSearch(response))
    const receiveNextPageErrorMW = (response) => dispatch(receiveNextPageError(response))
    const fetchSelectedUserSuccess = (generation) => (response) => {
      if (generation !== selectedProfileRequestGeneration) {
        return
      }

      SearchDrawerActions.setSelectedUser(response)
      dispatch(receiveSelectedUser(response))
    }

    const suggestedSearchesSuccess = (cb) => (resp) => {
      if (cb) {
        cb()
      }
      dispatch(receiveSuggestedSearches(resp))
    }

    const fetchSelectedUserError = (generation, cb) => () => {
      if (generation !== selectedProfileRequestGeneration) {
        return
      }

      SearchDrawerActions.setSelectedUser(null)
      dispatch(receiveSelectedUser(null))
      if (cb) {
        cb()
      }
    }

    const receiveFetchSearchSuggestionsSuccess = (response) => {
      dispatch(receiveSearchSuggestions(response.suggestedSearches))
    }

    const receiveRecentSearchesSuccess = (response) => {
      dispatch(receiveRecentSearches(response.searches))
    }

    let query = ""

    switch (action.type) {
      case FETCH_RECENT_SEARCHES:
        apiFetchRecentSearches(receiveRecentSearchesSuccess)
        return next(action)

      case RECEIVE_USER_SEARCH: {
        const response = action.response

        if (response.userInitiated === "true") {
          setTimeout(() => dispatch(fetchRecentSearches()), 500)
        }

        const oldState = store.getState().search.user
        if (oldState.query !== response.query) {
          // like reducer, do nothing more
          return next(action)
        }

        const results = handlePagination(action.response, oldState)

        const newFilters = { ...response.filters, ...action.filters }

        const newExplanation = updateExplanation(oldState, {
          results,
          filters: newFilters,
          loadingFilteredSearch: false, // from clearLoading in reducer
        })

        dispatch(
          receiveUserSearchFinal(
            response,
            action.selectedUserId,
            newFilters,
            newExplanation,
            results,
            oldState.query,
          ),
        )

        return next(action)
      }
      case FETCH_USER_SEARCH_NEXT_PAGE: {
        const { search } = store.getState()

        if (search.user.fetchingFirstPage) {
          return next(action)
        }
        query = search.user.query
        const { filters } = store.getState().search.user

        fetchSearchResults(
          { query, page: action.page, filters },
          receiveSearchResultsSuccess,
          receiveNextPageErrorMW,
        )
        return next(action)
      }

      case REFETCH_SEARCH: {
        const { filters } = store.getState().search.user

        fetchSearchResults(
          { query: store.getState().search.user.query, page: 0, filters },
          receiveRefetchSearchSuccess(action.success),
          receiveInitialSearchErrorMW,
        )
        dispatch(fetchMapLocations(store.getState().search.user.query, filters))
        return next(action)
      }

      case FETCH_MAP_LOCATIONS:
        apiFetchMapLocations(
          action.query,
          action.filters,
          receiveLocationsSuccess(action.success),
          action.error,
        )
        return next(action)

      case SET_SELECTED_USER: {
        const generation = ++selectedProfileRequestGeneration

        const user = findSelectedUser(
          store.getState().search.user.results,
          action.userId,
          action.customerId,
        )

        if (user) {
          action.user = user
          SearchDrawerActions.setSelectedUser(user)
          return next(action)
        }

        const result = next(action)
        dispatch(fetchSelectedUser(action.userId, action.customerId, action.error, generation))
        return result
      }

      case FETCH_SELECTED_USER:
        apiFetchSelectedUser(
          action.userId,
          action.customerId,
          fetchSelectedUserSuccess(action.generation),
          fetchSelectedUserError(action.generation, action.error),
        )
        return next(action)

      case SET_SELECTED_USER_URL: {
        const selectedUserVars = getUrlVars()

        selectedUserVars.id = action.userId
        if (action.customerId) {
          selectedUserVars.customerId = action.customerId
        }
        _history.push({
          ..._history.location,
          search: encodeQueryString(selectedUserVars),
        })
        addSuffix("profile")
        return next(action)
      }

      case FETCH_USER_SEARCH: {
        const fetchUserSearchVars = getUrlVars()
        let urlHasSameFilters = true
        let newFilters = {}
        const actionHasFilters =
          action.params.filters && Object.keys(action.params.filters).length !== 0
        const filterDict = store.getState().search.user?.filterDict

        Object.keys(filterDict).forEach((key) => {
          if (actionHasFilters) {
            if (fetchUserSearchVars[key] !== action.params.filters[key]) {
              newFilters[key] = fetchUserSearchVars[key]
              urlHasSameFilters = false
            }
          } else {
            newFilters = fetchUserSearchVars
            delete newFilters.query
            urlHasSameFilters = false
          }
        })

        const storeSearchResults = store.getState().search.user
        const aQueryIsActive =
          fetchUserSearchVars.query ||
          action.params.query ||
          action.params.query === "" ||
          Object.keys(newFilters).length !== 0 ||
          actionHasFilters

        if (
          aQueryIsActive &&
          (fetchUserSearchVars.query !== action.params.query || !urlHasSameFilters) &&
          !action.params.noRedirect
        ) {
          // Only handle navigation if history is provided (legacy action)
          // New clean action handles navigation in the custom hook
          if (action.history) {
            action.history.push({
              pathname: "/search",
              search: encodeQueryString({
                query: action.params.query,
                ...action.params.filters,
              }),
            })
          }
        }

        if (
          storeSearchResults.initialSearchError ||
          storeSearchResults.query !== action.params.query ||
          storeSearchResults.filters !== action.params.filters
        ) {
          action.params.userInitiated = true
          fetchSearchResults(
            action.params,
            receiveFetchUserSearch(
              Number.parseInt(fetchUserSearchVars.id, 10),
              action.params.filters,
              action.success,
            ),
            receiveInitialSearchErrorMW,
          )
          dispatch(fetchMapLocations(action.params.query, action.params.filters))
          action.fetching = true
        } else if (
          storeSearchResults.query === action.params.query &&
          storeSearchResults.filters === action.params.filters &&
          action.params.sendEmail &&
          storeSearchResults.results &&
          storeSearchResults.results.total > 0
        ) {
          // Do this so that when you hard refresh the search result page, you refetch search
          if (store.getState().search.user.searchAutocompleteSuggestions.length === 0) {
            action.params.userInitiated = true
            fetchSearchResults(
              action.params,
              receiveFetchUserSearch(
                Number.parseInt(fetchUserSearchVars.id, 10),
                action.params.filters,
                action.success,
              ),
              receiveInitialSearchErrorMW,
            )
            dispatch(fetchMapLocations(action.params.query, action.params.filters))
            action.fetching = true
          } else {
            // Legacy actions navigate here; clean actions complete through their callback.
            if (action.history) {
              action.history.push({ pathname: "/email/adminType" })
            } else if (action.success) {
              action.success()
            }
          }
        }
        return next(action)
      }

      case FETCH_SEARCH_TEXT_SUGGESTIONS:
        apiFetchSearchSuggestions(action.query, receiveFetchSearchSuggestionsSuccess)
        return next(action)

      case CLICK_SEARCH_CARD: {
        const { clickedUser, currentUser, params } = action

        if (clickedUser?.email === currentUser.email) {
          dispatch(NavigationActions.setSuffixLink("profile"))
        } else {
          dispatch(setSelectedUserUrl(clickedUser.constituentId, clickedUser.customerId))
        }
        dispatch(recordUserAction(params))
        return next(action)
      }

      case FETCH_SUGGESTED_SEARCHES:
        apiFetchSuggestedSearches(
          action.page,
          suggestedSearchesSuccess(action.success),
          action.error,
        )
        return next(action)

      case NavigationActions.clearSuffixLink.fulfilled.type: {
        dispatch(clearSelectedUser())
        return next(action)
      }

      case "CLEAR_SELECTED_USER":
        selectedProfileRequestGeneration += 1
        SearchDrawerActions.clearSelectedUser()
        VolunteerRolesActions.reset()
        return next(action)

      case "RECEIVE_CURRENT_CONSTITUENT":
        SearchDrawerActions.setSelectedUser(action.constituent)
        return next(action)

      default:
        return next(action)
    }
  }
}
